import {
  ShowWithTixologiEvent,
  getShowsByIds,
} from "@punchup/data/legacy/shows";

import { getTixologiEvents } from "@punchup/data/legacy/hooks";
import { useMemo } from "react";
import { useQuery, UseQueryResult } from "@tanstack/react-query";

export const SHOWS_WITH_TIXOLOGI_QUERY_KEY = "showsWithTixologiEvents" as const;

export async function fetchShowsWithTixologiEvents(
  showIds: string[]
): Promise<ShowWithTixologiEvent[]> {
  if (showIds.length === 0) return [];

  const showsMap = await getShowsByIds(showIds);
  const shows = showIds
    .map((id) => showsMap.get(id))
    .filter((s): s is NonNullable<typeof s> => !!s);

  const eventIds = shows
    .map((s) => s.tixologi_event_id)
    .filter((id): id is string => !!id);

  const events = eventIds.length
    ? await getTixologiEvents({
        ids: eventIds,
        date: "all",
        perPage: -1,
      })
    : [];

  const eventById = new Map(events.map((e) => [String(e.id), e]));

  return shows.map((show) => ({
    ...show,
    event: show.tixologi_event_id
      ? (eventById.get(String(show.tixologi_event_id)) ?? null)
      : null,
  }));
}

export function useShowsWithTixologiEvents(
  showIds: string[] | undefined
): UseQueryResult<ShowWithTixologiEvent[]> {
  // Sort the cache key so permuted-but-equivalent inputs hit the same entry.
  const sortedIds = useMemo(
    () => (showIds ? [...showIds].sort() : []),
    [showIds]
  );
  return useQuery<ShowWithTixologiEvent[]>({
    queryKey: [SHOWS_WITH_TIXOLOGI_QUERY_KEY, sortedIds],
    enabled: !!showIds && showIds.length > 0,
    staleTime: 3000,
    queryFn: () => fetchShowsWithTixologiEvents(showIds ?? []),
  });
}
